/**
 * tweestapsService — tweede stap bij inloggen (TOTP via een authenticator-app).
 *
 * Koppelen en de code controleren gaan rechtstreeks naar GoTrue (supabase.auth.mfa).
 * Wie de tweede stap moet doen, beslist de database (mijn_tweestaps_status, 105).
 * Herstelcodes en loskoppelen door beheer lopen via de backend (/api/tweestaps).
 * Pure regels staan in src/utils/tweestaps.js.
 */
import { supabase } from './supabase';
import { leesJwtClaims, bepaalStap } from '../utils/tweestaps';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend.testwisely.nl/api';
const ISSUER = 'TestWisely';
const BEWAAR_MS = 5 * 60 * 1000;

// Laatste uitkomst per gebruiker, zodat niet elke pagina opnieuw vraagt.
let bekend = null; // { gebruikerId, aal, uitkomst, tot }

/** Vergeet de laatste uitkomst (na koppelen, code, loskoppelen, uitloggen). */
export function vergeetStatus() {
  bekend = null;
}

try {
  supabase.auth.onAuthStateChange((event) => {
    if (event !== 'TOKEN_REFRESHED' && event !== 'INITIAL_SESSION') vergeetStatus();
  });
} catch { /* zonder auth-client (tests) is er niets te onthouden */ }

/** Uitkomst die nog geldt voor deze gebruiker, of null. Synchroon: geen laadscherm bij elke pagina. */
export function bekendeStatus(gebruikerId) {
  if (!bekend || !gebruikerId || bekend.gebruikerId !== gebruikerId || bekend.tot <= Date.now()) return null;
  return bekend.uitkomst;
}

/**
 * Wat moet deze gebruiker nu zien? { stap, status, aal, email }
 * stap: 'ok' | 'code' | 'koppelen' (zie bepaalStap).
 */
export async function haalTweestapsStatus({ vers = false } = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { stap: 'ok', status: null, aal: null, email: null };
  const gebruikerId = session.user?.id;
  const aal = leesJwtClaims(session.access_token)?.aal || null;
  if (!vers && bekend && bekend.gebruikerId === gebruikerId && bekend.aal === aal && bekend.tot > Date.now()) {
    return bekend.uitkomst;
  }
  let status = null;
  try {
    const { data, error } = await supabase.rpc('mijn_tweestaps_status');
    if (!error && data && typeof data === 'object') status = data;
  } catch { /* status blijft onbekend */ }
  let volgendeAal = null;
  if (!status) {
    try {
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      volgendeAal = data?.nextLevel || null;
    } catch { /* geen app bekend */ }
  }
  const uitkomst = { stap: bepaalStap({ aal, status, volgendeAal }), status, aal, email: session.user?.email || null };
  bekend = { gebruikerId, aal, uitkomst, tot: Date.now() + BEWAAR_MS };
  return uitkomst;
}

/** De gekoppelde (bevestigde) app, vers van de server. */
export async function gekoppeldeApp() {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  return (data?.totp || [])[0] || null;
}

/**
 * Begin met koppelen: ruimt half ingestelde apps op en vraagt een nieuwe QR-code.
 * Geeft { factorId, qrCode, geheim }.
 */
export async function beginKoppelen() {
  const { data: lijst } = await supabase.auth.mfa.listFactors();
  for (const f of (lijst?.all || [])) {
    if (f.status !== 'verified') {
      try { await supabase.auth.mfa.unenroll({ factorId: f.id }); } catch { /* verloopt vanzelf */ }
    }
  }
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    issuer: ISSUER,
    friendlyName: `Authenticator-app ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`,
  });
  if (error) throw error;
  return { factorId: data.id, qrCode: data.totp?.qr_code, geheim: data.totp?.secret };
}

/** Koppelen afgebroken: de half ingestelde app weer weg (anders verloopt hij na 5 minuten). */
export async function stopKoppelen(factorId) {
  if (!factorId) return;
  try { await supabase.auth.mfa.unenroll({ factorId }); } catch { /* verloopt vanzelf */ }
}

/** Code uit de app controleren (koppelen afronden of inloggen). Zet de sessie op AAL2. */
export async function bevestigCode(factorId, code) {
  const { data, error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
  vergeetStatus();
  if (error) throw error;
  return data;
}

/** Tweede stap uitzetten (alleen met de tweede stap, en alleen als hij niet verplicht is). */
export async function zetUit(factorId) {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  vergeetStatus();
  if (error) throw error;
  // Het token draagt nog aal2; vernieuwen zodat de rest van de app de nieuwe stand ziet.
  try { await supabase.auth.refreshSession(); } catch { /* volgende verversing doet het */ }
}

async function roep(method, pad, body = null) {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
  } catch { /* zonder token weigert de server */ }
  const res = await fetch(`${BACKEND_URL}/tweestaps/${pad}`, {
    method, headers, ...(body ? { body: JSON.stringify(body) } : {}),
  });
  let data = null;
  try { data = await res.json(); } catch { /* geen json */ }
  if (!res.ok) {
    const err = new Error(data?.error || `Server gaf ${res.status}`);
    err.status = res.status;
    err.code = data?.code;
    throw err;
  }
  return data;
}

/** Tien nieuwe herstelcodes (oude vervallen). Alleen met de tweede stap. */
export const maakHerstelcodes = () => roep('POST', 'herstelcodes').then((d) => d?.codes || []);

/** Telefoon kwijt: herstelcode gebruiken. Koppelt de app los; daarna een nieuwe koppelen. */
export async function gebruikHerstelcode(code) {
  const uit = await roep('POST', 'herstel', { code });
  vergeetStatus();
  // Het token kent de oude app nog; vernieuwen zodat GoTrue de nieuwe stand meegeeft.
  try { await supabase.auth.refreshSession(); } catch { /* volgende verversing doet het */ }
  return uit;
}

/** Schoolbeheer: { [docentId]: true/false } voor docenten met een wachtwoord. */
export const schoolOverzicht = () => roep('GET', 'school').then((d) => d?.docenten || {});
/** Schoolbeheer: app van een collega loskoppelen (telefoon kwijt). */
export const koppelLosVoorDocent = (docentId) => roep('DELETE', `docenten/${docentId}`);
/** TestWisely-beheer: zelfde, over alle scholen. */
export const beheerOverzicht = () => roep('GET', 'beheer').then((d) => d?.docenten || {});
export const koppelLosVoorAccount = (userId) => roep('DELETE', `beheer/${userId}`);
